import { Component, OnInit, OnChanges, SimpleChanges, inject, input, output, signal } from "@angular/core";
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Bookings } from "src/app/models/bookings";
import { ClientsService } from "src/app/services/clients.service";
import { MessageService } from "primeng/api";
import { COUNTRY_LIST, STATE_LIST } from "../../constants/constants";
import { catchError, map, Observable, of, switchMap, timer } from "rxjs";
import { Clients } from "src/app/models/clients";

import { Bind } from "primeng/bind";
import { InputText } from "primeng/inputtext";
import { Select } from "primeng/select";
import { ButtonDirective } from "primeng/button";
import { Toast } from "primeng/toast";
import { AddressSuggestComponent } from "../address-suggest/address-suggest.component";

import { TPipe } from "../../i18n/t.pipe";
import { PhoneInputComponent } from "../../components/phone-input/phone-input.component";
@Component({
    standalone: true,
    selector: "app-clients-form-detail",
    templateUrl: "./clients-form-detail.component.html",
    styleUrl: "./clients-form-detail.component.less",
    imports: [PhoneInputComponent, TPipe, FormsModule, ReactiveFormsModule, Bind, InputText, Select, ButtonDirective, Toast, AddressSuggestComponent]
})
export class ClientsFormDetailComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  private clientsService = inject(ClientsService);
  private messageService = inject(MessageService);

  readonly clientCreated = output<number>();

  readonly booking = input.required<Bookings>();
  readonly client = input<Clients | null>(null);
  readonly clientUpdated = output<Clients>();
  stateOptions = STATE_LIST;
  blogForm: FormGroup;
  readonly countries = signal<any[]>(undefined);
  readonly uploadError = signal<string>(undefined);
  constructor() {
    this.initializeForm();
    this.countries.set(COUNTRY_LIST);
  }

  ngOnInit(): void {}

  initializeForm() {
    this.blogForm = this.fb.group({
      id: [""],
      name: ["", Validators.required],
      surname: ["", Validators.required],
      address: [""],
      zip: [""],
      city: [""],
      email: [
        "",
        {
          validators: [Validators.required, Validators.email],
          asyncValidators: [
            this.emailExistsValidator(this.clientsService, () => this.blogForm?.get("id")?.value || null),
          ],
          updateOn: "blur",
        },
      ],
      phone: [""],
      is_active: [""],
      province: [""],
      country: [""],
      region: [""],
      lat: [""],
      lng: [""],
      fiscalcode: [""],
      description: [""],
      fiscalnumber: [""],
      date: [""],
      skills: [""],
      category_id: [""],
    });
  }
  emailExistsValidator(
    clientsService: ClientsService,
    getCurrentId: () => string | null,
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const email = (control.value || "").trim();
      if (!email) return of(null);

      const currentId = getCurrentId();

      const paramsRequests = {
        _start: 0,
        _limit: 1,
        is_booked: "0",
        email: email,
      };

      return timer(300).pipe(
        switchMap(() => clientsService.list(paramsRequests)),
        map((clients: Clients[]) => {
          if (!clients || clients.length === 0) {
            return null;
          }

          const exists = clients.some((c) => !currentId || c.id !== currentId);

          return exists ? { emailExists: true } : null;
        }),
        catchError(() => of(null)),
      );
    };
  }

  fillFormWithObject(booking: Bookings) {
    if (booking && this.blogForm) {
      this.blogForm.patchValue({
        name: booking?.name,
        surname: booking?.surname || "",
        address: booking?.address || "",
        zip: booking?.zip || "",
        city: booking?.city || "",
        email: booking?.email || "",
        phone: booking?.phone || "",
        province: booking?.province || "",
        country: booking?.country || "",
        fiscalcode: "",
        fiscalnumber: "",
        description: booking?.description || "",
        is_active: "1",
        category_id: "",
        date: booking?.date || "",
        skills: "",
      });
    } else {
      console.warn("Booking data is invalid or form is not initialized");
    }
  }

  private fillFormWithClient(c: Clients) {
    this.blogForm.patchValue(
      {
        id: c.id,
        name: c.name || "",
        surname: c.surname || "",
        email: c.email || "",
        phone: c.phone || "",
        address: c.address || "",
        zip: c.zip || "",
        city: c.city || "",
        province: c.province || "",
        region: (c as any).region || "",
        country: (c as any).country || "",
        lat: (c as any).lat || "",
        lng: (c as any).lng || "",
        fiscalcode: (c as any).fiscalcode || "",
        fiscalnumber: (c as any).fiscalnumber || "",
        is_active: (c as any).is_active ?? "1",
      },
      { emitEvent: false },
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["client"] && this.client()) {
      this.fillFormWithClient(this.client());
    }
    if (changes["booking"] && changes["booking"].currentValue) {
      this.fillFormWithObject(this.booking());
    }
  }

  onSubmitClient() {
    if (this.blogForm.valid) {
      const formData = new FormData();

      formData.append("name", this.blogForm.get("name")?.value || "");
      formData.append("surname", this.blogForm.get("surname")?.value || "");
      formData.append("address", this.blogForm.get("address")?.value || "");
      formData.append("zip", this.blogForm.get("zip")?.value || "");
      formData.append("city", this.blogForm.get("city")?.value || "");
      formData.append("email", this.blogForm.get("email")?.value || "");
      formData.append("phone", this.blogForm.get("phone")?.value || "");
      formData.append(
        "is_active",
        this.blogForm.get("is_active")?.value || "1",
      );
      formData.append("province", this.blogForm.get("province")?.value || "");
      formData.append("country", this.blogForm.get("country")?.value || "");
      formData.append("region", this.blogForm.get("region")?.value || "");
      formData.append("lat", this.blogForm.get("lat")?.value || "");
      formData.append("lng", this.blogForm.get("lng")?.value || "");
      formData.append(
        "fiscalcode",
        this.blogForm.get("fiscalcode")?.value || "",
      );
      formData.append(
        "description",
        this.blogForm.get("description")?.value || "",
      );
      formData.append(
        "fiscalnumber",
        this.blogForm.get("fiscalnumber")?.value || "",
      );
      formData.append("date", this.blogForm.get("date")?.value || "");
      formData.append(
        "skills",
        JSON.stringify(this.blogForm.get("skills")?.value || ""),
      );
      formData.append(
        "is_featured",
        this.blogForm.get("is_featured")?.value || "0",
      );
      formData.append(
        "category_id",
        this.blogForm.get("category_id")?.value || "",
      );

      const id = this.blogForm.get("id")?.value;

      if (id) {

        this.clientsService.update(formData, id).subscribe(
          (res) => {
            console.log("Server Response:", res);
            if (res.status === "error") {
              this.uploadError.set(res.message);
              this.messageService.add({
                key: "myKey1",
                severity: "error",
                summary: "Errore",
                detail: res.message,
              });
            } else {
              this.messageService.add({
                key: "myKey1",
                severity: "success",
                summary: "Successo",
                detail: "Utente aggiornato con successo",
              });
              this.clientsService.getId(id).subscribe((saved) => this.clientUpdated.emit(saved));
            }
          },
          (error) => {
            console.error("Error during user update:", error);
            this.messageService.add({
              key: "myKey1",
              severity: "error",
              summary: "Errore",
              detail: "Errore durante l'aggiornamento dell'utente",
            });
          },
        );
      } else {

        this.clientsService.create(formData).subscribe(
          (res) => {
            console.log("Server Response:", res);
            if (res.status === "error") {
              this.uploadError.set(res.message);
              this.messageService.add({
                key: "myKey1",
                severity: "error",
                summary: "Errore",
                detail: res.message,
              });
            } else {
              this.messageService.add({
                key: "myKey1",
                severity: "success",
                summary: "Successo",
                detail: "Utente creato con successo",
              });

              const newClientId = res.id;
              this.clientCreated.emit(newClientId);
              this.blogForm.reset();
            }
          },
          (error) => {
            console.error("Error during user creation:", error);
            this.messageService.add({
              key: "myKey1",
              severity: "error",
              summary: "Errore",
              detail: "Errore durante la creazione dell'utente",
            });
          },
        );
      }
    } else {
      console.warn("Form is invalid:", this.blogForm.value);
      this.messageService.add({
        key: "myKey1",
        severity: "warn",
        summary: "Form non valido",
        detail: "Controlla i campi obbligatori",
      });
    }
  }
}
